import { useState, useEffect, useCallback } from 'react';
import { quoteExplorerAPI } from '../../services/api';

const GROUP_OPTIONS = [
  { value: 'month', label: 'By Month' },
  { value: 'week', label: 'By Week' },
  { value: 'day', label: 'By Day' },
  { value: 'lane', label: 'By Shipping Lane' },
  { value: 'provider', label: 'By Provider' },
  { value: 'quoteType', label: 'By Quote Type' },
  { value: 'containerType', label: 'By Container Type' },
  { value: 'incoterm', label: 'By Incoterm' },
  { value: 'status', label: 'By Status' },
  { value: 'source', label: 'By Source' }
];

const SORT_OPTIONS = [
  { value: 'createdAt', label: 'Date' },
  { value: 'totalCost', label: 'Cost' },
  { value: 'transitDays', label: 'Transit Days' },
  { value: 'providerName', label: 'Provider' },
  { value: 'status', label: 'Status' }
];

const defaultFilters = () => ({
  dateFrom: (() => { const d = new Date(); d.setMonth(d.getMonth() - 6); return d.toISOString().split('T')[0]; })(),
  dateTo: new Date().toISOString().split('T')[0],
  groupBy: 'month',
  quoteType: '',
  originPort: '',
  destinationPort: '',
  providerId: '',
  status: '',
  minCost: '',
  maxCost: '',
  minTransitDays: '',
  maxTransitDays: '',
  containerType: '',
  commodity: '',
  incoterm: '',
  source: '',
  sortBy: 'createdAt',
  sortDir: 'DESC',
  page: 1,
  limit: 25
});

const QuoteExplorer = () => {
  const [filters, setFilters] = useState(defaultFilters);
  const [options, setOptions] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [activeTab, setActiveTab] = useState('groups'); // 'groups' | 'quotes'

  // Load filter options on mount
  useEffect(() => {
    quoteExplorerAPI.getOptions()
      .then(res => setOptions(res.data))
      .catch(() => {});
  }, []);

  // Query on filters change (debounced via explicit search)
  const doSearch = useCallback(async (overrides = {}) => {
    setLoading(true);
    try {
      const params = { ...filters, ...overrides };
      // Strip empty values
      const clean = {};
      Object.entries(params).forEach(([k, v]) => {
        if (v !== '' && v !== undefined && v !== null) clean[k] = v;
      });
      const res = await quoteExplorerAPI.query(clean);
      setResult(res.data);
    } catch (e) {
      // Keep previous results visible on error
    }
    setLoading(false);
  }, [filters]);

  // Initial search
  useEffect(() => {
    doSearch();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateFilter = (key, value) => {
    setFilters(prev => {
      const next = { ...prev, [key]: value, page: key === 'page' ? value : 1 };
      // Origin/destination options depend on quoteType; clear stale selections
      // so a value that disappears from the dropdown isn't silently applied.
      if (key === 'quoteType') {
        next.originPort = '';
        next.destinationPort = '';
      }
      return next;
    });
  };

  const handleSearch = () => {
    doSearch({ ...filters, page: 1 });
    setFilters(prev => ({ ...prev, page: 1 }));
  };

  const handleReset = () => {
    const d = defaultFilters();
    setFilters(d);
    doSearch(d);
  };

  const handlePageChange = (newPage) => {
    const updated = { ...filters, page: newPage };
    setFilters(updated);
    doSearch(updated);
  };

  const handleSort = (field) => {
    const newDir = filters.sortBy === field && filters.sortDir === 'DESC' ? 'ASC' : 'DESC';
    const updated = { ...filters, sortBy: field, sortDir: newDir, page: 1 };
    setFilters(updated);
    doSearch(updated);
  };

  const handleExport = async () => {
    try {
      const clean = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== '' && v !== undefined && v !== null) clean[k] = v;
      });
      const response = await quoteExplorerAPI.exportCsv(clean);
      const blob = new Blob([response], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `quote-explorer-${filters.dateTo}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Failed to export CSV.');
    }
  };

  const sortArrow = (field) => {
    if (filters.sortBy !== field) return '';
    return filters.sortDir === 'ASC' ? ' \u25B2' : ' \u25BC';
  };

  const fmtCurrency = (val) => {
    if (val == null) return '-';
    return '$' + Number(val).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  const fmtCurrencyFull = (val) => {
    if (val == null) return '-';
    return '$' + Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div>
      <div className="page-header">
        <h1>Quote Explorer</h1>
        <div className="header-actions">
          <button className="btn btn-sm btn-secondary" onClick={() => setShowFilters(f => !f)}>
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
          <button className="btn btn-sm btn-secondary" onClick={handleExport}>
            Export CSV
          </button>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="dashboard-card" style={{ marginBottom: '1rem' }}>
          <div className="qe-filters">
            {/* Row 1: Date range + Group by */}
            <div className="qe-filter-row">
              <div className="qe-filter-group">
                <label className="qe-label">From</label>
                <input type="date" className="filter-input" value={filters.dateFrom}
                  onChange={e => updateFilter('dateFrom', e.target.value)} />
              </div>
              <div className="qe-filter-group">
                <label className="qe-label">To</label>
                <input type="date" className="filter-input" value={filters.dateTo}
                  onChange={e => updateFilter('dateTo', e.target.value)} />
              </div>
              <div className="qe-filter-group">
                <label className="qe-label">Group By</label>
                <select className="filter-select" value={filters.groupBy}
                  onChange={e => updateFilter('groupBy', e.target.value)}>
                  {GROUP_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div className="qe-filter-group">
                <label className="qe-label">Quote Type</label>
                <select className="filter-select" value={filters.quoteType}
                  onChange={e => updateFilter('quoteType', e.target.value)}>
                  <option value="">All Types</option>
                  <option value="ocean_freight">Ocean Freight</option>
                  <option value="domestic_trucking">Domestic Trucking</option>
                </select>
              </div>
            </div>

            {/* Row 2: Lane + Provider + Status */}
            <div className="qe-filter-row">
              <div className="qe-filter-group">
                <label className="qe-label">Origin</label>
                <select className="filter-select" value={filters.originPort}
                  onChange={e => updateFilter('originPort', e.target.value)}>
                  <option value="">All Origins</option>
                  {options && [...new Set(
                    options.lanes
                      .filter(l => !filters.quoteType || l.quoteType === filters.quoteType)
                      .map(l => l.origin)
                  )].filter(Boolean).sort().map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div className="qe-filter-group">
                <label className="qe-label">Destination</label>
                <select className="filter-select" value={filters.destinationPort}
                  onChange={e => updateFilter('destinationPort', e.target.value)}>
                  <option value="">All Destinations</option>
                  {options && [...new Set(
                    options.lanes
                      .filter(l => !filters.quoteType || l.quoteType === filters.quoteType)
                      .map(l => l.destination)
                  )].filter(Boolean).sort().map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div className="qe-filter-group">
                <label className="qe-label">Provider</label>
                <select className="filter-select" value={filters.providerId}
                  onChange={e => updateFilter('providerId', e.target.value)}>
                  <option value="">All Providers</option>
                  {options && options.providers.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div className="qe-filter-group">
                <label className="qe-label">Status</label>
                <select className="filter-select" value={filters.status}
                  onChange={e => updateFilter('status', e.target.value)}>
                  <option value="">All Statuses</option>
                  {options && options.statuses.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Row 3: Cost range + Transit + Container + Source */}
            <div className="qe-filter-row">
              <div className="qe-filter-group">
                <label className="qe-label">Min Cost ($)</label>
                <input type="number" className="filter-input" placeholder="0"
                  value={filters.minCost} onChange={e => updateFilter('minCost', e.target.value)} />
              </div>
              <div className="qe-filter-group">
                <label className="qe-label">Max Cost ($)</label>
                <input type="number" className="filter-input" placeholder="Any"
                  value={filters.maxCost} onChange={e => updateFilter('maxCost', e.target.value)} />
              </div>
              <div className="qe-filter-group">
                <label className="qe-label">Min Transit Days</label>
                <input type="number" className="filter-input" placeholder="0"
                  value={filters.minTransitDays} onChange={e => updateFilter('minTransitDays', e.target.value)} />
              </div>
              <div className="qe-filter-group">
                <label className="qe-label">Max Transit Days</label>
                <input type="number" className="filter-input" placeholder="Any"
                  value={filters.maxTransitDays} onChange={e => updateFilter('maxTransitDays', e.target.value)} />
              </div>
            </div>

            {/* Row 4: Container type + Commodity + Incoterm + Source */}
            <div className="qe-filter-row">
              <div className="qe-filter-group">
                <label className="qe-label">Container Type</label>
                <select className="filter-select" value={filters.containerType}
                  onChange={e => updateFilter('containerType', e.target.value)}>
                  <option value="">All Containers</option>
                  {options && options.containerTypes.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div className="qe-filter-group">
                <label className="qe-label">Commodity</label>
                <input type="text" className="filter-input" placeholder="Search..."
                  value={filters.commodity} onChange={e => updateFilter('commodity', e.target.value)} />
              </div>
              <div className="qe-filter-group">
                <label className="qe-label">Incoterm</label>
                <select className="filter-select" value={filters.incoterm}
                  onChange={e => updateFilter('incoterm', e.target.value)}>
                  <option value="">All Incoterms</option>
                  {options && options.incoterms.map(i => (
                    <option key={i} value={i}>{i}</option>
                  ))}
                </select>
              </div>
              <div className="qe-filter-group">
                <label className="qe-label">Source</label>
                <select className="filter-select" value={filters.source}
                  onChange={e => updateFilter('source', e.target.value)}>
                  <option value="">All Sources</option>
                  {options && options.sources.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Action buttons */}
            <div className="qe-filter-actions">
              <button className="btn btn-primary" onClick={handleSearch} disabled={loading}>
                {loading ? 'Searching...' : 'Search'}
              </button>
              <button className="btn btn-secondary" onClick={handleReset}>Reset</button>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {result && (
        <div className="dashboard-card" style={{ marginBottom: '1rem' }}>
          <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
            <div className="stat-item">
              <span className="stat-value">{result.summary.totalQuotes}</span>
              <span className="stat-label">Total Quotes</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{fmtCurrency(result.summary.avgCost)}</span>
              <span className="stat-label">Avg Cost</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{fmtCurrency(result.summary.minCost)}</span>
              <span className="stat-label">Min Cost</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{fmtCurrency(result.summary.maxCost)}</span>
              <span className="stat-label">Max Cost</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{fmtCurrency(result.summary.totalCostSum)}</span>
              <span className="stat-label">Total Spend</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{result.summary.avgTransitDays}d</span>
              <span className="stat-label">Avg Transit</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{result.summary.minTransitDays}-{result.summary.maxTransitDays}d</span>
              <span className="stat-label">Transit Range</span>
            </div>
          </div>

          {/* Breakdown chips */}
          <div className="qe-breakdown-row">
            {Object.keys(result.summary.byQuoteType).length > 0 && (
              <div className="qe-chips">
                <span className="qe-chip-label">By Type:</span>
                {Object.entries(result.summary.byQuoteType).map(([type, count]) => (
                  <span key={type} className="qe-chip">
                    {type === 'ocean_freight' ? 'Ocean' : 'Trucking'}: {count}
                  </span>
                ))}
              </div>
            )}
            {Object.keys(result.summary.byStatus).length > 0 && (
              <div className="qe-chips">
                <span className="qe-chip-label">By Status:</span>
                {Object.entries(result.summary.byStatus).map(([status, count]) => (
                  <span key={status} className="qe-chip">{status}: {count}</span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tab toggle: Groups vs Individual Quotes */}
      {result && (
        <div className="qe-tabs">
          <button
            className={`qe-tab ${activeTab === 'groups' ? 'active' : ''}`}
            onClick={() => setActiveTab('groups')}>
            Grouped Breakdown ({result.groups.length})
          </button>
          <button
            className={`qe-tab ${activeTab === 'quotes' ? 'active' : ''}`}
            onClick={() => setActiveTab('quotes')}>
            Individual Quotes ({result.pagination.total})
          </button>
        </div>
      )}

      {/* Grouped Breakdown Table */}
      {result && activeTab === 'groups' && (
        <div className="dashboard-card">
          {result.groups.length > 0 ? (
            <table className="simple-table">
              <thead>
                <tr>
                  <th>{GROUP_OPTIONS.find(o => o.value === filters.groupBy)?.label || 'Group'}</th>
                  <th>Quotes</th>
                  <th>Avg Cost</th>
                  <th>Min Cost</th>
                  <th>Max Cost</th>
                  <th>Total Cost</th>
                  <th>Avg Transit</th>
                </tr>
              </thead>
              <tbody>
                {result.groups.map((g, i) => (
                  <tr key={i}>
                    <td><strong>{g.label}</strong></td>
                    <td>{g.count}</td>
                    <td>{fmtCurrencyFull(g.avgCost)}</td>
                    <td>{fmtCurrencyFull(g.minCost)}</td>
                    <td>{fmtCurrencyFull(g.maxCost)}</td>
                    <td>{fmtCurrencyFull(g.totalCost)}</td>
                    <td>{g.avgTransitDays}d</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-muted">No grouped data available for the selected filters.</p>
          )}
        </div>
      )}

      {/* Individual Quotes Table */}
      {result && activeTab === 'quotes' && (
        <div className="dashboard-card">
          {result.quotes.length > 0 ? (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table className="simple-table">
                  <thead>
                    <tr>
                      <th className="sortable-th" onClick={() => handleSort('createdAt')}>
                        Date{sortArrow('createdAt')}
                      </th>
                      <th className="sortable-th" onClick={() => handleSort('providerName')}>
                        Provider{sortArrow('providerName')}
                      </th>
                      <th>Type</th>
                      <th className="sortable-th" onClick={() => handleSort('totalCost')}>
                        Total Cost{sortArrow('totalCost')}
                      </th>
                      <th className="sortable-th" onClick={() => handleSort('transitDays')}>
                        Transit{sortArrow('transitDays')}
                      </th>
                      <th>Lane</th>
                      <th>Container</th>
                      <th>Commodity</th>
                      <th className="sortable-th" onClick={() => handleSort('status')}>
                        Status{sortArrow('status')}
                      </th>
                      <th>Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.quotes.map(q => (
                      <tr key={q.id}>
                        <td>{q.createdAt ? new Date(q.createdAt).toLocaleDateString() : '-'}</td>
                        <td>{q.providerName}</td>
                        <td>{q.quoteType === 'ocean_freight' ? 'Ocean' : 'Trucking'}</td>
                        <td>{fmtCurrencyFull(q.totalCost)}</td>
                        <td>{q.transitDays ? `${q.transitDays}d` : '-'}</td>
                        <td>{q.originPort && q.destinationPort ? `${q.originPort} - ${q.destinationPort}` : '-'}</td>
                        <td>{q.containerType || '-'}{q.containerCount > 1 ? ` x${q.containerCount}` : ''}</td>
                        <td className="qe-commodity-cell">{q.commodity || '-'}</td>
                        <td><span className={`status-badge status-${q.status?.toLowerCase()}`}>{q.status}</span></td>
                        <td>{q.source || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {result.pagination.totalPages > 1 && (
                <div className="qe-pagination">
                  <button className="btn btn-sm btn-secondary"
                    disabled={result.pagination.page <= 1}
                    onClick={() => handlePageChange(result.pagination.page - 1)}>
                    Previous
                  </button>
                  <span className="qe-page-info">
                    Page {result.pagination.page} of {result.pagination.totalPages}
                    {' '}({result.pagination.total} quotes)
                  </span>
                  <button className="btn btn-sm btn-secondary"
                    disabled={result.pagination.page >= result.pagination.totalPages}
                    onClick={() => handlePageChange(result.pagination.page + 1)}>
                    Next
                  </button>
                </div>
              )}
            </>
          ) : (
            <p className="text-muted">No quotes match the selected filters.</p>
          )}
        </div>
      )}

      {loading && !result && (
        <div className="dashboard-card">
          <p className="loading">Loading quote data...</p>
        </div>
      )}
    </div>
  );
};

export default QuoteExplorer;
